"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { getUserLocale } from "@/services/locale";

type TCustomPaginationProps = {
  pages: number;
  current: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCurrentPage: React.Dispatch<React.SetStateAction<number>> | any;
};

const getPageRange = (current: number, total: number) => {
  const delta = 1;
  const range: number[] = [];

  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  range.push(1);

  if (left > 2) {
    range.push(-1); // ellipsis
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < total - 1) {
    range.push(-2); // ellipsis
  }

  if (total > 1) {
    range.push(total);
  }

  return range;
};

const CustomPagination = ({
  pages,
  current,
  setCurrentPage,
}: TCustomPaginationProps) => {
  const range = getPageRange(current, pages);
  const [locale, setLocale] = useState("");

  const handleLocale = useCallback(async () => {
    const locale = await getUserLocale();
    setLocale(locale);
  }, []);

  useEffect(() => {
    handleLocale();
  }, [handleLocale]);

  return (
    <Pagination>
      <PaginationContent>
        <PaginationPrevious
          className=" hover:text-white"
          onClick={() => setCurrentPage(current - 1)}
          disabled={current === 1}
          locale={locale}
        />

        {range.map((page) =>
          page === -1 || page === -2 ? (
            <PaginationEllipsis key={page} />
          ) : (
            <PaginationItem key={page}>
              <PaginationLink
                className=" hover:text-white"
                onClick={() => setCurrentPage(page)}
                isActive={page === current}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationNext
          className=" hover:text-white"
          onClick={() => setCurrentPage(current + 1)}
          disabled={current === pages}
          locale={locale}
        />
      </PaginationContent>
    </Pagination>
  );
};

export default CustomPagination;
